import Alert from '@/components/Alert/Alert'
import TableView, { TableSection } from '@/components/TableView'
import { destroySubAccountThunk, SubAccount, updateSubAccountThunk } from '@/redux/slices/subAccountSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { PlanCode, UserRole, UserStatus } from '@/typings/domain'
import { handleClickToPay } from '@/modules/handleClickToPay'
import { useCallback, useMemo } from 'react'

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.Owner]: '拥有者',
  [UserRole.Admin]: '管理员',
  [UserRole.Worker]: '协作者',
  [UserRole.Outworker]: '查看员',
}

const AVAILABLE_ROLES: UserRole[] = [UserRole.Admin, UserRole.Worker, UserRole.Outworker]

type Props = {
  user: SubAccount
  isCurrentUser?: boolean
  isOwner?: boolean
  canEdit?: boolean
  onRemoveSuccess?: () => void
}

export default function UserDetail({
  user,
  isCurrentUser = false,
  isOwner = false,
  canEdit = true,
  onRemoveSuccess,
}: Props) {
  const dispatch = useAppDispatch()
  const usageAndBalance = useAppSelector(state => state.usageAndBalance)
  const currentUser = useAppSelector(state => state.currentUser.user)

  const handleRolePress = useCallback(
    (role: UserRole) => {
      if (role === user.role) return
      dispatch(updateSubAccountThunk({ id: user.id, role }))
    },
    [user.role, user.id, dispatch]
  )

  const handleToggleStatus = useCallback(() => {
    const name = user.name || user.email || user.mobile || '该成员'
    const newStatus = user.status === UserStatus.Active ? UserStatus.Disabled : UserStatus.Active
    const statusText = newStatus === UserStatus.Disabled ? '禁用' : '启用'

    const hasAddSubAccountAbility = currentUser?.billingAccountAbilities?.hasAddSubAccountAbility
    const availableQuota = usageAndBalance.usersQuota - usageAndBalance.occupiedUsersCount

    if (newStatus === UserStatus.Active) {
      if (!hasAddSubAccountAbility) {
        handleClickToPay({ recommendedPlanCode: PlanCode.E10, currentUser: currentUser ?? undefined })
        return
      }

      if (availableQuota <= 0) {
        Alert.alert(
          '套餐余量不足',
          `当前已使用 ${usageAndBalance.occupiedUsersCount} 个企业成员，${availableQuota < 0 ? `超出配额上限 ${Math.abs(availableQuota)} 个` : '已达到配额上限'}。请禁用其他成员，或者购买更多名额。`,
          [{ text: '我知道了', style: 'cancel' }]
        )
        return
      }
    }

    Alert.alert(`${statusText}成员`, `确定要${statusText} ${name} 吗？`, [
      { text: '取消', style: 'cancel' },
      {
        text: '确定',
        style: newStatus === UserStatus.Disabled ? 'destructive' : 'default',
        onPress: async () => {
          await dispatch(updateSubAccountThunk({ id: user.id, status: newStatus }))
        },
      },
    ])
  }, [user, dispatch, usageAndBalance, currentUser])

  const handleRemovePress = useCallback(() => {
    const name = user.name || user.email || user.mobile || '该成员'

    Alert.alert('删除成员', `确定要删除 ${name} 吗？此操作无法撤销。`, [
      { text: '取消', style: 'cancel' },
      {
        text: '删除',
        style: 'destructive',
        onPress: async () => {
          await dispatch(destroySubAccountThunk({ id: user.id }))
          onRemoveSuccess?.()
        },
      },
    ])
  }, [user, dispatch, onRemoveSuccess])

  const sections = useMemo<TableSection[]>(() => {
    const groups: TableSection[] = []

    // Section 1: User info
    const displayName = user.name || user.email || user.mobile || '未命名'
    const subtitle = [user.email, user.mobile].filter(Boolean).join(' | ')

    groups.push({
      title: '',
      rows: [
        {
          title: displayName,
          subTitle: subtitle || undefined,
          icon: user.avatarUrl ?? undefined,
        },
      ],
    })

    // Section 2: Stats
    const formCount = user.createdFormsCount || 0
    const entryCount = user.entriesCountOfCreatedForms || 0

    groups.push({
      title: '统计',
      rows: [
        {
          title: '创建表单',
          detailText: `${formCount}`,
        },
        {
          title: '收集数据',
          detailText: `${entryCount.toLocaleString()}`,
        },
      ],
    })

    // Section 3: Roles (radio selection for editable users, static for owner/current user)
    if (isOwner) {
      groups.push({
        title: '权限',
        rows: [
          {
            title: ROLE_LABELS[UserRole.Owner],
          },
        ],
      })
    } else if (isCurrentUser || !canEdit) {
      groups.push({
        title: '权限',
        rows: [
          {
            title: user.role ? ROLE_LABELS[user.role] : '未设置',
          },
        ],
      })
    } else {
      groups.push({
        title: '权限',
        rows: AVAILABLE_ROLES.map(role => ({
          title: ROLE_LABELS[role],
          type: 'radio' as const,
          radioSelected: role === user.role,
          onPress: () => handleRolePress(role),
        })),
      })
    }

    // Section 4: Actions (only for editable users who are not current user or owner)
    if (!isCurrentUser && !isOwner && canEdit) {
      const isDisabled = user.status === UserStatus.Disabled

      groups.push({
        title: '',
        rows: [
          {
            title: isDisabled ? '启用成员' : '禁用成员',
            type: 'action' as const,
            onPress: handleToggleStatus,
          },
        ],
      })

      groups.push({
        title: '',
        rows: [
          {
            title: '删除成员',
            type: 'action' as const,
            actionType: 'destructive' as const,
            onPress: handleRemovePress,
          },
        ],
      })
    }

    return groups
  }, [user, isOwner, isCurrentUser, canEdit, handleRolePress, handleToggleStatus, handleRemovePress])

  return <TableView sections={sections} tableViewStyle="insetGrouped" />
}
